/**
 * Live Shopping Calendar
 * ----------------------
 * Drives sections/live-shopping-calendar.liquid. Renders a horizontal date
 * strip plus "upcoming" / "past shows" card grids for the selected day, fed
 * by a Bambuser-Channels-API-shaped array of show objects.
 *
 * The fetch layer (fetchShows) and the render layer (renderSchedule /
 * groupShowsByDate / renderCalendar / renderUpcoming / renderPast /
 * createShowCard) are kept independent on purpose: swapping the fallback
 * JSON for a live endpoint only ever touches fetchShows.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-live-shopping-calendar]';

  /* ------------------------------------------------------------------ *
   * Date / formatting utilities
   * ------------------------------------------------------------------ */

  /** Parses an API date string/number into a Date, or null if invalid. */
  function toDate(value) {
    if (!value) return null;
    var date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  /** 'YYYY-MM-DD' key for a Date, in the *browser's* local timezone. */
  function getLocalDateKey(date) {
    var year = date.getFullYear();
    var month = String(date.getMonth() + 1).padStart(2, '0');
    var day = String(date.getDate()).padStart(2, '0');
    return year + '-' + month + '-' + day;
  }

  /** Inverse of getLocalDateKey — builds a local midnight Date from the key. */
  function dateKeyToDate(key) {
    var parts = key.split('-').map(Number);
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  function addDays(date, amount) {
    var next = new Date(date);
    next.setDate(next.getDate() + amount);
    return next;
  }

  function formatMonth(date, locale) {
    return new Intl.DateTimeFormat(locale || 'en', { month: 'long' }).format(date);
  }

  function formatWeekdayShort(date, locale) {
    return new Intl.DateTimeFormat(locale || 'en', { weekday: 'short' }).format(date);
  }

  function formatWeekdayLong(date, locale) {
    return new Intl.DateTimeFormat(locale || 'en', { weekday: 'long' }).format(date);
  }

  function formatMonthShort(date, locale) {
    return new Intl.DateTimeFormat(locale || 'en', { month: 'short' }).format(date);
  }

  /**
   * "7:30 PM" — always rendered 12-hour with upper-case AM/PM regardless of
   * storefront locale, since that's the format the design calls for.
   */
  function formatTime(date) {
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).format(date);
  }

  /** Generic date formatter used wherever a { month, weekday, day } shape is needed. */
  function formatDate(date, locale) {
    return {
      month: formatMonth(date, locale),
      monthShort: formatMonthShort(date, locale),
      weekdayShort: formatWeekdayShort(date, locale),
      weekdayLong: formatWeekdayLong(date, locale),
      day: date.getDate(),
    };
  }

  function sanitizeUrl(url) {
    if (typeof url !== 'string' || url === '') return null;
    if (url.indexOf('//') === 0) return 'https:' + url;
    return url;
  }

  /* ------------------------------------------------------------------ *
   * Show normalization — maps a raw Bambuser Channels API show object
   * into the flat shape the renderer needs. Every field is read
   * defensively since none of it is guaranteed to be present.
   * ------------------------------------------------------------------ */

  function isShowEnded(raw) {
    return raw.state === 'ended';
  }

  /** The date a show is grouped/filed under — always scheduledStartAt, per the API contract. */
  function getScheduledDate(raw) {
    return toDate(raw.scheduledStartAt) || toDate(raw.startedAt) || toDate(raw.endedAt);
  }

  /** The time actually shown on the card — the real start time once one exists. */
  function getDisplayTimestamp(raw) {
    return toDate(raw.startedAt) || toDate(raw.scheduledStartAt) || toDate(raw.endedAt);
  }

  /**
   * The real Bambuser Channels API returns `preview` as a plain string URL
   * on each show (the channel itself separately has a `previewImages`
   * array — that's not per-show and isn't used here).
   */
  function getPreviewUrl(raw) {
    return typeof raw.preview === 'string' ? sanitizeUrl(raw.preview) : null;
  }

  function normalizeShow(raw) {
    var scheduledDate = getScheduledDate(raw);
    if (!raw || !raw.id || !scheduledDate) return null;

    var ended = isShowEnded(raw);
    var displayTimestamp = getDisplayTimestamp(raw);

    return {
      id: String(raw.id),
      title: raw.title || 'Untitled show',
      description: raw.description || '',
      previewUrl: getPreviewUrl(raw),
      dateKey: getLocalDateKey(scheduledDate),
      sortTimestamp: scheduledDate,
      displayTimestamp: displayTimestamp,
      hasStarted: Boolean(toDate(raw.startedAt)),
      isLive: raw.isLive === true,
      isEnded: ended,
      isReplay: raw.isArchived === true,
      // The API's `channels`/`contributors` arrays are opaque ID strings —
      // Bambuser doesn't expose a host display name or avatar image on the
      // show object, so the avatar is always a placeholder initial.
      avatars: [{ name: raw.title || '', avatarUrl: null }],
      productCount: Array.isArray(raw.products) ? raw.products.length : 0,
      watchUrl: null,
      raw: raw,
    };
  }

  /* ------------------------------------------------------------------ *
   * Pure grouping / filtering helpers
   * ------------------------------------------------------------------ */

  function groupShowsByDate(shows) {
    var map = new Map();
    shows.forEach(function (show) {
      if (!map.has(show.dateKey)) map.set(show.dateKey, []);
      map.get(show.dateKey).push(show);
    });
    map.forEach(function (list) {
      list.sort(function (a, b) {
        return a.sortTimestamp - b.sortTimestamp;
      });
    });
    return map;
  }

  function filterShows(shows, predicate) {
    return shows.filter(predicate);
  }

  /* ------------------------------------------------------------------ *
   * Data source
   * ------------------------------------------------------------------
   * fetchShows() only knows how to get the raw channel payload (from the
   * live Bambuser Channels API, or the embedded fallback JSON if that
   * request fails). It does not know anything about playlists or show
   * shapes — that's normalizeBambuserResponse()'s job, kept as a separate
   * step so fetchShows can later be swapped for a call to a server-side
   * proxy without touching any of the parsing/rendering code below.
   * ------------------------------------------------------------------ */

  function readFallbackData(root) {
    var node = root.querySelector('[data-lsc="fallback-data"]');
    if (!node) return null;
    try {
      return JSON.parse((node.content ? node.content.textContent : node.textContent).trim() || 'null');
    } catch (error) {
      console.error('[live-shopping-calendar] invalid fallback JSON', error);
      return null;
    }
  }

  /** Reads Channel 1 / Channel 2 ids off the root element, trimmed, blanks dropped. */
  function getChannelIds(root) {
    return [root.dataset.apiChannelId1, root.dataset.apiChannelId2].map(function (id) { return (id || '').trim(); }).filter(Boolean);
  }

  /** Fetches + flattens a single channel's raw payload into an array of raw show objects. */
  function fetchChannelShows(base, channelId, token) {
    var endpoint = base.replace(/\/$/, '') + '/' + encodeURIComponent(channelId);
    var headers = { Accept: 'application/json' };
    // TEMPORARY (dev/testing only): calling the Bambuser Channels API
    // directly from the browser with a plain-text token. Before shipping,
    // replace this fetch with a call to a server-side/app-proxy endpoint
    // that holds the token instead — nothing outside this function needs
    // to change when that happens.
    if (token) headers.Authorization = 'Token ' + token;

    return fetch(endpoint, { method: 'GET', headers: headers })
      .then(function (response) {
        if (!response.ok) throw new Error('Bambuser API request failed with status ' + response.status + ' for channel ' + channelId);
        return response.json();
      })
      .then(normalizeBambuserResponse)
      .catch(function (error) {
        console.error('[live-shopping-calendar] channel fetch failed, skipping:', channelId, error);
        return [];
      });
  }

  /**
   * Fetches every configured channel (Channel 1 / Channel 2), flattens each
   * one's playlists, and merges the results into a single de-duplicated
   * array of raw show objects. Falls back to the embedded JSON if there's
   * no usable channel config, or if every channel request failed.
   */
  function fetchShows(root) {
    var base = root.dataset.apiBase;
    var token = root.dataset.apiToken;
    var channelIds = getChannelIds(root);
    if (!base || !channelIds.length) {
      return Promise.resolve(normalizeBambuserResponse(readFallbackData(root)));
    }

    return Promise.all(
      channelIds.map(function (id) { return fetchChannelShows(base, id, token); })
    ).then(function (perChannelShows) {
      var seenIds = new Set();
      var merged = [];
      perChannelShows.forEach(function (shows) {
        shows.forEach(function (show) {
          if (!show || !show.id || seenIds.has(show.id)) return; // de-dupe across channels
          seenIds.add(show.id);
          merged.push(show);
        });
      });
      return merged.length ? merged : normalizeBambuserResponse(readFallbackData(root));
    });
  }

  /**
   * Flattens a raw Bambuser channel payload (`{ playlists: [{ shows: [...] }] }`)
   * into a single de-duplicated array of raw show objects. A show can appear
   * in more than one playlist (e.g. "Featured" alongside "Upcoming shows"),
   * so later occurrences of an already-seen id are dropped.
   */
  function normalizeBambuserResponse(payload) {
    if (!payload || !Array.isArray(payload.playlists)) return [];

    var seenIds = new Set();
    var shows = [];

    payload.playlists.forEach(function (playlist) {
      var playlistShows = (playlist && Array.isArray(playlist.shows)) ? playlist.shows : [];
      playlistShows.forEach(function (show) {
        if (!show || !show.id || seenIds.has(show.id)) return;
        seenIds.add(show.id);
        shows.push(show);
      });
    });

    return shows;
  }

  /* ------------------------------------------------------------------ *
   * Main controller
   * ------------------------------------------------------------------ */
  function LiveShoppingCalendar(root) {
    this.root = root;
    this.locale = root.dataset.locale || document.documentElement.lang || 'en';
    this.daysBefore = parseInt(root.dataset.daysBefore, 10) || 0;
    this.daysAfter = parseInt(root.dataset.daysAfter, 10) || 7;
    this.labels = {
      today: root.dataset.i18nToday || 'Today',
      tomorrow: root.dataset.i18nTomorrow || 'Tomorrow',
      pastShows: root.dataset.i18nPastShows || 'Past Shows',
      returnToday: root.dataset.i18nReturnToday || 'Return to Today',
      notify: root.dataset.i18nNotify || 'Notify Me',
      notified: root.dataset.i18nNotified || 'Notified',
      watch: root.dataset.i18nWatch || 'Watch Livestream',
      replay: root.dataset.i18nReplay || 'Watch Replay',
      empty: root.dataset.i18nEmpty || 'No shows scheduled for this day yet.',
    };

    this.els = {
      calendarNav: root.querySelector('[data-lsc="calendar"]'),
      calendarTrack: root.querySelector('[data-lsc="calendar-track"]'),
      monthLabel: root.querySelector('[data-lsc="month"]'),
      returnTodayBtn: root.querySelector('[data-lsc="return-today"]'),
      dayHeading: root.querySelector('.lsc__day-heading'),
      dayLabel: root.querySelector('[data-lsc="day-label"]'),
      dayDate: root.querySelector('[data-lsc="day-date"]'),
      status: root.querySelector('[data-lsc="status"]'),
      upcomingGrid: root.querySelector('[data-lsc="upcoming-grid"]'),
      pastSection: root.querySelector('[data-lsc="past-section"]'),
      pastGrid: root.querySelector('[data-lsc="past-grid"]'),
    };

    this.cardTemplate = document.getElementById('live-show-card-template');

    this.today = new Date();
    this.todayKey = getLocalDateKey(this.today);
    this.selectedKey = this.todayKey;
    this.groupedByDate = new Map();
    this.dateRange = [];

    this.bindEvents();
    this.load();
  }

  LiveShoppingCalendar.prototype.load = function () {
    this.showStatus('Loading schedule…');
    var self = this;
    fetchShows(this.root).then(function (rawShows) {
      self.renderSchedule(rawShows);
    });
  };

  /** Entry point once raw API/fallback data is available. */
  LiveShoppingCalendar.prototype.renderSchedule = function (rawShows) {
    var shows = (rawShows || [])
      .filter(function (raw) {
        return raw && raw.published !== false;
      })
      .map(normalizeShow)
      .filter(Boolean);

    this.groupedByDate = groupShowsByDate(shows);
    this.dateRange = this.buildDateRange();
    this.hideStatus();
    this.renderCalendar();
    this.renderSelectedDay();
  };

  LiveShoppingCalendar.prototype.buildDateRange = function () {
    var range = [];
    for (var offset = -this.daysBefore; offset <= this.daysAfter; offset++) {
      range.push(addDays(this.today, offset));
    }
    return range;
  };

  /** Builds the date strip. Runs once per data load — never per day-click. */
  LiveShoppingCalendar.prototype.renderCalendar = function () {
    var self = this;
    var fragment = document.createDocumentFragment();

    this.dateRange.forEach(function (date) {
      var key = getLocalDateKey(date);
      var isToday = key === self.todayKey;
      var isSelected = key === self.selectedKey;

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'lsc-day-btn' + (isToday ? ' lsc-day-btn--today' : '') + (isSelected ? ' lsc-day-btn--selected' : '');
      button.dataset.dateKey = key;
      if (isSelected) button.setAttribute('aria-current', 'date');

      var weekday = document.createElement('span');
      weekday.className = 'lsc-day-btn__weekday';
      weekday.textContent = formatWeekdayShort(date, self.locale);

      var number = document.createElement('span');
      number.className = 'lsc-day-btn__number';
      number.textContent = String(date.getDate());

      button.append(weekday, number);
      fragment.appendChild(button);
    });

    this.els.calendarTrack.replaceChildren(fragment);
  };

  /** Only touches the day heading + the two card grids — never the calendar. */
  LiveShoppingCalendar.prototype.renderSelectedDay = function () {
    var selectedDate = dateKeyToDate(this.selectedKey);
    if (this.els.monthLabel) {
      this.els.monthLabel.textContent = formatMonth(selectedDate, this.locale).toUpperCase();
    }
    this.els.dayLabel.textContent = this.getDayLabel(selectedDate);

    var monthSpan = document.createElement('span');
    monthSpan.className = 'lsc__day-date-month';
    monthSpan.textContent = formatMonthShort(selectedDate, this.locale).toUpperCase();
    var numberSpan = document.createElement('span');
    numberSpan.className = 'lsc__day-date-number';
    numberSpan.textContent = String(selectedDate.getDate());
    this.els.dayDate.replaceChildren(monthSpan, numberSpan);

    this.els.returnTodayBtn.hidden = this.selectedKey === this.todayKey;

    // 'YYYY-MM-DD' keys sort lexicographically, so a plain string compare
    // tells us if the selected day is strictly before today.
    var isPastDate = this.selectedKey < this.todayKey;
    var showsForDay = this.groupedByDate.get(this.selectedKey) || [];
    this.renderUpcoming(filterShows(showsForDay, this.isUpcoming), isPastDate);
    this.renderPast(filterShows(showsForDay, this.isPast));
  };

  LiveShoppingCalendar.prototype.isUpcoming = function (show) {
    return !show.isEnded;
  };

  LiveShoppingCalendar.prototype.isPast = function (show) {
    return show.isEnded;
  };

  LiveShoppingCalendar.prototype.getDayLabel = function (date) {
    var key = getLocalDateKey(date);
    if (key === this.todayKey) return this.labels.today;
    if (key === getLocalDateKey(addDays(this.today, 1))) return this.labels.tomorrow;
    return formatWeekdayLong(date, this.locale).toUpperCase();
  };

  LiveShoppingCalendar.prototype.renderUpcoming = function (shows, isPastDate) {
    // Past dates never show the "No shows scheduled" empty state — if there's
    // nothing upcoming, the whole grid hides and Past Shows takes its place.
    if (isPastDate && shows.length === 0) {
      this.els.upcomingGrid.hidden = true;
      this.els.upcomingGrid.replaceChildren();
      return;
    }
    this.els.upcomingGrid.hidden = false;
    this.renderGrid(this.els.upcomingGrid, shows, { showEmptyMessage: true });
  };

  LiveShoppingCalendar.prototype.renderPast = function (shows) {
    this.els.pastSection.hidden = shows.length === 0;
    this.renderGrid(this.els.pastGrid, shows, { showEmptyMessage: false });
  };

  LiveShoppingCalendar.prototype.renderGrid = function (grid, shows, options) {
    if (!shows.length) {
      if (options && options.showEmptyMessage) {
        var empty = document.createElement('p');
        empty.className = 'lsc__empty';
        empty.textContent = this.labels.empty;
        grid.replaceChildren(empty);
      } else {
        grid.replaceChildren();
      }
      return;
    }

    var fragment = document.createDocumentFragment();
    var self = this;
    shows.forEach(function (show) {
      fragment.appendChild(self.createShowCard(show));
    });
    grid.replaceChildren(fragment);
  };

  /** Clones the <template> from snippets/live-show-card.liquid and fills it in. */
  LiveShoppingCalendar.prototype.createShowCard = function (show) {
    var node = this.cardTemplate.content.firstElementChild.cloneNode(true);
    node.dataset.showId = show.id;
    node.dataset.productsCount = String(show.productCount);

    if (show.isLive) node.classList.add('lsc-card--live');
    if (show.isEnded) node.classList.add('lsc-card--past');

    this.fillMedia(node, show);
    // this.fillAvatars(node, show);
    this.fillText(node, show);
    this.fillInfoRows(node, show);
    this.fillFooter(node, show);

    return node;
  };

  /** Curtain image — always sourced from curtains.pending.backgroundImage, falling back to preview. */
  function getCurtainImageUrl(show) {
    var curtains = (show.raw && show.raw.curtains) || {};
    var url = curtains.pending && curtains.pending.backgroundImage;
    return sanitizeUrl(url || show.previewUrl);
  }

  LiveShoppingCalendar.prototype.fillMedia = function (node, show) {
    var media = node.querySelector('.lsc-card__media');
    var image = node.querySelector('[data-field="image"]');
    var logo = node.querySelector('[data-field="fallback-logo"]');
    var bgUrl = getCurtainImageUrl(show);

    if (bgUrl) {
      media.style.setProperty('--lsc-card-bg-image', 'url(' + JSON.stringify(bgUrl) + ')');
      image.hidden = true;
      logo.hidden = true;
    } else {
      media.style.removeProperty('--lsc-card-bg-image');
      image.hidden = true;
      logo.hidden = false;
    }
    // No badge data exists on the Bambuser show object today — the
    // template's [data-field="badge"] element stays hidden unless a
    // future API revision adds one.
  };

  LiveShoppingCalendar.prototype.fillAvatars = function (node, show) {
    var container = node.querySelector('[data-field="avatars"]');
    var fragment = document.createDocumentFragment();

    show.avatars.forEach(function (avatar) {
      var el;
      if (avatar.avatarUrl) {
        el = document.createElement('img');
        el.className = 'lsc-card__avatar';
        el.src = avatar.avatarUrl;
        el.alt = avatar.name || '';
        el.loading = 'lazy';
      } else {
        el = document.createElement('span');
        el.className = 'lsc-card__avatar';
        el.setAttribute('aria-hidden', 'true');
        el.textContent = (avatar.name || show.title || '?').trim().charAt(0).toUpperCase();
      }
      fragment.appendChild(el);
    });

    container.replaceChildren(fragment);
  };

  LiveShoppingCalendar.prototype.fillText = function (node, show) {
    node.querySelector('[data-field="title"]').textContent = show.title;

    var subtitle = node.querySelector('[data-field="subtitle"]');
    if (show.description) {
      subtitle.textContent = show.description;
      subtitle.hidden = false;
    }
  };

  LiveShoppingCalendar.prototype.fillInfoRows = function (node, show) {
    var timeLabel = show.hasStarted ? 'Started' : 'Starts';
    node.querySelector('[data-field="time"]').textContent = timeLabel + ' @ ' + formatTime(show.displayTimestamp);

    // The Bambuser show object doesn't expose a host/channel display name
    // (channels[] and contributors[] are opaque IDs), so the "hosted by"
    // row from snippets/live-show-card.liquid stays hidden — there's
    // nothing truthful to put in it yet.
  };

  LiveShoppingCalendar.prototype.fillFooter = function (node, show) {
    var watchBtn = node.querySelector('[data-action="watch"]');
    var notifyBtn = node.querySelector('[data-action="notify"]');
    var replayBtn = node.querySelector('[data-action="replay"]');

    if (show.isLive) {
      watchBtn.hidden = false;
      watchBtn.querySelector('[data-field="watch-label"]').textContent = this.labels.watch;
    } else if (show.isEnded) {
      if (show.isReplay) {
        replayBtn.hidden = false;
        replayBtn.querySelector('[data-field="replay-label"]').textContent = this.labels.replay;
      }
    } else {
      notifyBtn.hidden = false;
      notifyBtn.setAttribute('aria-haspopup', 'true');
      notifyBtn.setAttribute('aria-expanded', 'false');
      notifyBtn.querySelector('[data-field="notify-label"]').textContent = this.labels.notify;
    }

    var footer = node.querySelector('[data-field="footer"]');
    footer.hidden = watchBtn.hidden && notifyBtn.hidden && replayBtn.hidden;
  };

  /* ------------------------------------------------------------------ *
   * Events — delegated so selecting a day / toggling a card never binds
   * new per-node listeners.
   * ------------------------------------------------------------------ */

  LiveShoppingCalendar.prototype.bindEvents = function () {
    var self = this;

    this.els.calendarTrack.addEventListener('click', function (event) {
      var button = event.target.closest('.lsc-day-btn');
      if (!button) return;
      self.selectDate(button.dataset.dateKey);
    });

    this.els.returnTodayBtn.addEventListener('click', function () {
      self.selectDate(self.todayKey);
    });

    this.root.addEventListener('click', function (event) {
      var actionButton = event.target.closest('[data-action]');
      var card = event.target.closest('[data-show-card]');
      if (!card) return;

      if (actionButton) {
        switch (actionButton.dataset.action) {
          case 'notify':
            self.handleNotifyClick(actionButton, card);
            return;
          case 'watch':
          case 'replay':
            self.openShow(card);
            return;
          case 'menu':
            return;
        }
      }
      // Clicking anywhere on a Live or Ended card (not on the Notify button) redirects.
      if (card.classList.contains('lsc-card--live') || card.classList.contains('lsc-card--past')) {
        self.openShow(card);
      }
    });
  };

  /** Swaps the selected pill and re-renders only the cards for the new day. */
  LiveShoppingCalendar.prototype.selectDate = function (dateKey) {
    if (!dateKey) return;

    if (dateKey === this.selectedKey) {
      // Clicking the already-selected date (e.g. Today, from a sticky
      // calendar further down the page) shouldn't re-render anything — just
      // bring the show list back into view.
      this.scrollToShowList();
      return;
    }

    var previous = this.els.calendarTrack.querySelector('[data-date-key="' + this.selectedKey + '"]');
    var next = this.els.calendarTrack.querySelector('[data-date-key="' + dateKey + '"]');

    if (previous) {
      previous.classList.remove('lsc-day-btn--selected');
      previous.removeAttribute('aria-current');
    }
    if (next) {
      next.classList.add('lsc-day-btn--selected');
      next.setAttribute('aria-current', 'date');
      next.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }

    this.selectedKey = dateKey;
    this.renderSelectedDay();
    this.scrollToShowList();
  };

  /** Bottom edge of the sticky calendar nav in viewport coordinates — content must clear this to be unobstructed. */
  LiveShoppingCalendar.prototype.getStickyOffset = function () {
    var nav = this.els.calendarNav;
    return nav ? Math.max(0, nav.getBoundingClientRect().bottom) : 0;
  };

  /**
   * Scrolls to the day heading (just above whichever grid ends up visible) only
   * if it isn't already fully visible below the sticky calendar nav — never
   * scrolls to the page top, and never scrolls at all when nothing is hidden.
   */
  LiveShoppingCalendar.prototype.scrollToShowList = function () {
    var target = this.els.dayHeading;
    if (!target) return;

    var offset = this.getStickyOffset();
    var rect = target.getBoundingClientRect();
    var alreadyVisible = rect.top >= offset && rect.bottom <= window.innerHeight;
    if (alreadyVisible) return;

    var scrollTop = window.pageYOffset + rect.top - offset - 50;
    window.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
  };

  /** 'YYYYMMDDTHHMMSSZ' — the UTC format Google/Outlook/ICS calendar links expect. */
  function toCalendarUtc(date) {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  }

  /** Inline SVGs for the calendar dropdown — no icon font/CDN. */
  var CAL_ICONS = {
    google:
      '<svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.616z"/>' +
      '<path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>' +
      '<path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A9.001 9.001 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>' +
      '<path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.581-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/>' +
      '</svg>',
    apple:
      '<svg width="18" height="18" viewBox="0 0 16 16" fill="#111111" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M11.182 6.783c-.02-1.98 1.618-2.931 1.692-2.977-.924-1.35-2.36-1.535-2.87-1.556-1.222-.124-2.386.72-3.005.72-.62 0-1.577-.702-2.593-.683-1.334.02-2.564.776-3.25 1.972-1.387 2.403-.355 5.96.996 7.912.66.955 1.446 2.027 2.478 1.988.996-.04 1.372-.643 2.577-.643 1.204 0 1.542.643 2.596.62 1.072-.018 1.749-.972 2.404-1.93.759-1.106 1.072-2.178 1.09-2.234-.024-.011-2.09-.802-2.115-3.19z"/>' +
      '<path d="M9.53 1.02c.546-.66.913-1.58.813-2.5-.784.032-1.732.523-2.297 1.181-.505.583-.946 1.522-.827 2.417.874.068 1.766-.443 2.311-1.098z"/>' +
      '</svg>',
    outlook:
      '<svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<rect width="18" height="18" rx="3" fill="#0072C6"/>' +
      '<path fill="#fff" d="M9 4.5a4.2 4.2 0 1 0 0 8.4 4.2 4.2 0 0 0 0-8.4zm0 6.7a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/>' +
      '</svg>',
    download:
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111111" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M12 3v12"/><path d="M7 10l5 5 5-5"/><path d="M5 21h14"/>' +
      '</svg>',
  };

  /** Smart placement: below by default, flips above when there isn't room, and always clamps inside the viewport. */
  function positionCalendarMenu(button, menu) {
    var GAP = 8;
    var rect = button.getBoundingClientRect();
    var menuHeight = menu.offsetHeight;
    var menuWidth = menu.offsetWidth;
    var viewportWidth = document.documentElement.clientWidth;
    var viewportHeight = document.documentElement.clientHeight;

    var spaceBelow = viewportHeight - rect.bottom;
    var spaceAbove = rect.top;
    var openAbove = spaceBelow < menuHeight + GAP && spaceAbove > spaceBelow;

    var top = openAbove ? rect.top - menuHeight - GAP : rect.bottom + GAP;
    top = Math.max(GAP, Math.min(top, viewportHeight - menuHeight - GAP));

    var left = Math.max(GAP, Math.min(rect.left, viewportWidth - menuWidth - GAP));

    menu.style.top = Math.round(top) + 'px';
    menu.style.left = Math.round(left) + 'px';
    menu.classList.toggle('lsc-cal-menu--above', openAbove);
  }

  /**
   * The show's Bambuser API response has no dedicated "watch URL" field
   * today (only opaque id/channels/contributors) — dedicated/watchUrl are
   * checked first so this keeps working if a future API revision adds one,
   * but for now it always builds the deep link from the real show ID.
   */
  function getShowUrl(show) {
    var raw = show.raw || {};
    var dedicated = sanitizeUrl(raw.url || raw.showUrl || raw.shareUrl || show.watchUrl);
    if (dedicated) return dedicated;
    return 'https://luxurypromise.com/pages/live-shopping?autoplayLiveShopping=' + encodeURIComponent(show.id);
  }

  /** Google/Outlook link data for a show, built entirely from the normalized Bambuser show object. */
  function buildCalendarLinks(show) {
    var start = (show.raw && toDate(show.raw.scheduledStartAt)) || show.displayTimestamp;
    var endedAt = show.raw && toDate(show.raw.endedAt);
    var end = endedAt || new Date(start.getTime() + ((show.raw && show.raw.showLengthSeconds) || 3600) * 1000);
    var title = encodeURIComponent(show.title || 'Luxury Promise Live Shopping');
    var showUrl = getShowUrl(show);
    var descriptionText = (show.description ? show.description + '\n\n' : '') + 'Watch Live:\n' + showUrl;
    var details = encodeURIComponent(descriptionText);
    var s = toCalendarUtc(start), e = toCalendarUtc(end);
    return {
      start: start,
      end: end,
      descriptionText: descriptionText,
      showUrl: showUrl,
      google: 'https://calendar.google.com/calendar/render?action=TEMPLATE&text=' + title + '&dates=' + s + '/' + e + '&details=' + details,
      outlook: 'https://outlook.live.com/calendar/0/deeplink/compose?subject=' + title + '&startdt=' + start.toISOString() + '&enddt=' + end.toISOString() + '&body=' + details,
    };
  }

  /** Builds an .ics file in-memory (no library) and triggers a download via a Blob URL. */
  function downloadIcs(show, links) {
    var content = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'BEGIN:VEVENT',
      'UID:' + show.id + '@luxurypromise.com',
      'DTSTART:' + toCalendarUtc(links.start),
      'DTEND:' + toCalendarUtc(links.end),
      'SUMMARY:' + (show.title || 'Luxury Promise Live Shopping'),
      'DESCRIPTION:' + links.descriptionText.replace(/\n/g, '\\n'),
      'URL:' + links.showUrl,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
    var blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
    var url = URL.createObjectURL(blob);
    var link = document.createElement('a');
    link.href = url;
    link.download = (show.title || 'show').replace(/[^a-z0-9-_]+/gi, '-') + '.ics';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
  }

  LiveShoppingCalendar.prototype.closeCalendarMenu = function () {
    if (this.calendarMenuButton) {
      this.calendarMenuButton.setAttribute('aria-expanded', 'false');
      this.calendarMenuButton = null;
    }
    if (this.boundCloseOnEsc) {
      document.removeEventListener('keydown', this.boundCloseOnEsc);
      this.boundCloseOnEsc = null;
    }
    if (this.boundReposition) {
      window.removeEventListener('scroll', this.boundReposition, true);
      window.removeEventListener('resize', this.boundReposition);
      this.boundReposition = null;
    }
    if (this.calendarMenu) {
      var menu = this.calendarMenu;
      this.calendarMenu = null;
      menu.classList.remove('lsc-cal-menu--open');
      setTimeout(function () { menu.remove(); }, 180);
    }
  };

  /**
   * Renders the dropdown to document.body (not as a card child) and positions it
   * with fixed coordinates from the button's own rect. Every .lsc-card clips its
   * content with `overflow: hidden` (for the rounded corners / media crop), so a
   * menu inserted as a card descendant is created successfully but rendered
   * invisible — that clipping, not a JS failure, was the actual bug.
   */
  LiveShoppingCalendar.prototype.openCalendarMenu = function (button, show) {
    console.log('[LSC] Opening calendar menu for show:', show.id, show.title);
    var links = buildCalendarLinks(show);
    console.log('[LSC] Calendar links generated:', links);

    var menu = document.createElement('div');
    menu.className = 'lsc-cal-menu';
    menu.setAttribute('role', 'menu');
    menu.innerHTML =
      '<a role="menuitem" href="' + links.google + '" target="_blank" rel="noopener"><span class="lsc-cal-menu__icon">' + CAL_ICONS.google + '</span><span>Google Calendar</span></a>' +
      '<button type="button" role="menuitem" class="lsc-cal-menu__btn" data-cal-action="ics"><span class="lsc-cal-menu__icon">' + CAL_ICONS.apple + '</span><span>Apple Calendar</span></button>' +
      '<a role="menuitem" href="' + links.outlook + '" target="_blank" rel="noopener"><span class="lsc-cal-menu__icon">' + CAL_ICONS.outlook + '</span><span>Outlook Calendar</span></a>' +
      '<button type="button" role="menuitem" class="lsc-cal-menu__btn" data-cal-action="ics"><span class="lsc-cal-menu__icon">' + CAL_ICONS.download + '</span><span>Download ICS</span></button>';

    menu.querySelectorAll('[data-cal-action="ics"]').forEach(function (el) {
      el.addEventListener('click', function () { downloadIcs(show, links); });
    });

    document.body.appendChild(menu);
    console.log('[LSC] Menu inserted into DOM:', menu);

    var self = this;
    var reposition = function () { positionCalendarMenu(button, menu); };
    reposition();
    requestAnimationFrame(function () { menu.classList.add('lsc-cal-menu--open'); });

    this.calendarMenu = menu;
    this.calendarMenuButton = button;
    button.setAttribute('aria-expanded', 'true');

    this.boundCloseOnEsc = function (event) {
      if (event.key === 'Escape') {
        self.closeCalendarMenu();
        button.focus();
      }
    };
    document.addEventListener('keydown', this.boundCloseOnEsc);

    // Reposition (rather than close) on scroll/resize — `true` catches scroll
    // events from any nested scrollable ancestor (cart drawer, calendar list).
    this.boundReposition = reposition;
    window.addEventListener('scroll', this.boundReposition, true);
    window.addEventListener('resize', this.boundReposition);

    setTimeout(function () {
      document.addEventListener('click', function onDoc(event) {
        if (menu.contains(event.target) || event.target === button) return;
        self.closeCalendarMenu();
        document.removeEventListener('click', onDoc);
      });
    }, 0);
  };

  /** Notify Me click → immediately opens the calendar dropdown. No "Notified" state, no localStorage. */
  LiveShoppingCalendar.prototype.handleNotifyClick = function (button, card) {
    console.log('[LSC] Notify button clicked for show id:', card.dataset.showId);
    var showId = card.dataset.showId;
    var isSameButton = this.calendarMenuButton === button;
    this.closeCalendarMenu();
    if (isSameButton) return;

    var show = Array.from(this.groupedByDate.values()).flat().find(function (candidate) {
      return candidate.id === showId;
    });

    if (!show) {
      console.error('[LSC] No matching show found for id', showId, '— cannot open calendar menu.');
      return;
    }
    console.log('[LSC] Show found:', show);
    this.openCalendarMenu(button, show);
  };

  /** Live/Ended cards & buttons redirect to the storefront's autoplay deep link. */
  LiveShoppingCalendar.prototype.openShow = function (card) {
    window.location.href = 'https://luxurypromise.com/pages/live-shopping?autoplayLiveShopping=' + encodeURIComponent(card.dataset.showId);
  };

  LiveShoppingCalendar.prototype.showStatus = function (text) {
    this.els.status.hidden = false;
    this.els.status.textContent = text;
  };

  LiveShoppingCalendar.prototype.hideStatus = function () {
    this.els.status.hidden = true;
  };

  /* ------------------------------------------------------------------ *
   * Bootstrap
   * ------------------------------------------------------------------ */

  function init(context) {
    (context || document).querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      if (root.__liveShoppingCalendar) return;
      root.__liveShoppingCalendar = new LiveShoppingCalendar(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Re-init inside the theme editor when this section is added/reloaded.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();